// Auth store — current Supabase session + sign-in/out helpers.
//
// Phase 1 auth: email+password is the primary method; magic-link is
// kept as a backup / passwordless alternative. Anonymous users keep
// working — the dual-mode stores treat `session == null` as
// "local-only mode" and the UI exposes a "Sign in to sync" CTA.
//
// On sign-in:
//   1. We hydrate `profile` from app.profiles (auto-created by the
//      handle_new_user trigger).
//   2. Other stores subscribe to `session` changes and trigger their
//      own initial pull/push logic.
//   3. localStorage persists the session via supabase-js's built-in
//      auth.persistSession setting.

import { create } from 'zustand';
import type { Session, User } from '@supabase/supabase-js';
import { supabase, supabaseConfigured } from '../lib/supabase';

export interface ProfileRow {
  id: string;
  display_name: string | null;
  email: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
}

interface AuthState {
  /** True once the initial session check has finished (whether logged
   *  in or not). Stores use this to delay reads until auth resolves. */
  ready: boolean;
  /** True when Supabase env vars are present; false otherwise. */
  configured: boolean;
  session: Session | null;
  user: User | null;
  profile: ProfileRow | null;
  signingIn: boolean;
  signInError: string | null;
  lastMagicLinkSentTo: string | null;
  /**
   * Set to true when refreshSession() rejects on resume (e.g. token
   * expired while the device was locked / LTE was dead). Triggers a
   * non-blocking amber banner in AppShell so the pilot knows their
   * writes are not reaching the server. Clears on successful refresh
   * or explicit dismiss.
   * HI-5' audit 2026-05-24.
   */
  sessionStale: boolean;

  /** Mark the session as stale (refresh failed) or fresh (refresh succeeded). */
  setSessionStale: (stale: boolean) => void;
  /** Hydrate the initial session + subscribe to changes. Idempotent. */
  init: () => Promise<void>;
  /** Primary sign-in: email + password. */
  signInWithPassword: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  /** Sign up with email + password. Returns session if email_confirm is
   *  disabled; returns no session (ok: true, needsConfirm: true) if
   *  Supabase requires email confirmation. */
  signUpWithPassword: (email: string, password: string) => Promise<{ ok: boolean; needsConfirm?: boolean; error?: string }>;
  /** Sends a password-reset email (recovery link). */
  sendPasswordReset: (email: string) => Promise<{ ok: boolean; error?: string }>;
  /** Updates the current user's password (used in the reset landing page). */
  updatePassword: (newPassword: string) => Promise<{ ok: boolean; error?: string }>;
  /** Fallback / passwordless: sends a magic-link OTP email. */
  sendMagicLink: (email: string) => Promise<{ ok: boolean; error?: string }>;
  signOut: () => Promise<void>;
  /** Re-fetch profile row (used after sign-in or after manual edits). */
  refreshProfile: () => Promise<void>;
  /** Update the display name (writes to app.profiles). */
  updateProfile: (patch: Partial<Pick<ProfileRow, 'display_name' | 'avatar_url'>>) => Promise<void>;
}

let initialized = false;
// Stores the Supabase auth subscription handle so we can unsubscribe
// before re-attaching (prevents HMR double-listener on every file save).
let authSubscription: { unsubscribe: () => void } | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
  ready: false,
  configured: supabaseConfigured,
  session: null,
  user: null,
  profile: null,
  signingIn: false,
  signInError: null,
  lastMagicLinkSentTo: null,
  sessionStale: false,

  setSessionStale: (stale) => set({ sessionStale: stale }),

  init: async () => {
    if (initialized) return;
    initialized = true;

    if (!supabase) {
      set({ ready: true });
      return;
    }

    // Pull current session (may be null for first-time / signed-out).
    const { data: { session } } = await supabase.auth.getSession();
    set({ session, user: session?.user ?? null });
    if (session) {
      await get().refreshProfile();
      void resolvePendingInvites();
    }

    // Subscribe to future auth changes. Store the handle so HMR
    // can unsubscribe before re-attaching (prevents double-listener
    // on every Vite HMR cycle). CO2 fix 2026-05-13.
    authSubscription?.unsubscribe();
    const { data: _authSubData } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      set({ session: newSession, user: newSession?.user ?? null });
      if (newSession) {
        await get().refreshProfile();
        // Multi-org invitation flow: every sign-in (whether interactive
        // or a magic-link acceptance) checks for pending invites that
        // match this user's email and resolves them. The RPC is
        // idempotent, so calling it on every SIGNED_IN / TOKEN_REFRESHED
        // event is safe.
        if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'USER_UPDATED') {
          void resolvePendingInvites();
        }
      } else {
        set({ profile: null });
      }
    });
    authSubscription = _authSubData.subscription;

    set({ ready: true });
  },

  signInWithPassword: async (email, password) => {
    if (!supabase) return { ok: false, error: 'Supabase not configured' };
    set({ signingIn: true, signInError: null });
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        set({ signingIn: false, signInError: error.message });
        return { ok: false, error: error.message };
      }
      // onAuthStateChange handles session + profile hydration.
      set({ signingIn: false });
      return { ok: true };
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ signingIn: false, signInError: msg });
      return { ok: false, error: msg };
    }
  },

  signUpWithPassword: async (email, password) => {
    if (!supabase) return { ok: false, error: 'Supabase not configured' };
    set({ signingIn: true, signInError: null });
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: typeof window !== 'undefined'
            ? `${window.location.origin}/auth/supabase/callback`
            : undefined,
        },
      });
      if (error) {
        set({ signingIn: false, signInError: error.message });
        return { ok: false, error: error.message };
      }
      set({ signingIn: false });
      // If Supabase returns a session immediately, email_confirm is off.
      if (data.session) return { ok: true, needsConfirm: false };
      // No session = confirmation email sent.
      return { ok: true, needsConfirm: true };
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ signingIn: false, signInError: msg });
      return { ok: false, error: msg };
    }
  },

  sendPasswordReset: async (email) => {
    if (!supabase) return { ok: false, error: 'Supabase not configured' };
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: typeof window !== 'undefined'
          ? `${window.location.origin}/auth/supabase/reset`
          : undefined,
      });
      if (error) return { ok: false, error: error.message };
      return { ok: true };
    } catch (e: unknown) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) };
    }
  },

  updatePassword: async (newPassword) => {
    if (!supabase) return { ok: false, error: 'Supabase not configured' };
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) return { ok: false, error: error.message };
      return { ok: true };
    } catch (e: unknown) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) };
    }
  },

  sendMagicLink: async (email) => {
    if (!supabase) return { ok: false, error: 'Supabase not configured' };
    set({ signingIn: true, signInError: null });
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          // After the user clicks the link in their email, send them
          // back to the app's auth callback so we can complete the
          // session exchange. Same pattern as the AGOL callback we
          // already have wired.
          emailRedirectTo: typeof window !== 'undefined'
            ? `${window.location.origin}/auth/supabase/callback`
            : undefined,
        },
      });
      if (error) {
        set({ signingIn: false, signInError: error.message });
        return { ok: false, error: error.message };
      }
      set({ signingIn: false, lastMagicLinkSentTo: email });
      return { ok: true };
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ signingIn: false, signInError: msg });
      return { ok: false, error: msg };
    }
  },

  signOut: async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    // onAuthStateChange will clear session + profile.
  },

  refreshProfile: async () => {
    if (!supabase) return;
    const user = get().user;
    if (!user) {
      set({ profile: null });
      return;
    }
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[auth] profile fetch failed', error.message);
      return;
    }
    if (data) set({ profile: data as ProfileRow });
  },

  updateProfile: async (patch) => {
    if (!supabase) return;
    const user = get().user;
    if (!user) return;
    const { data, error } = await supabase
      .from('profiles')
      .update({ ...patch })
      .eq('id', user.id)
      .select()
      .maybeSingle();
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[auth] profile update failed', error.message);
      return;
    }
    if (data) set({ profile: data as ProfileRow });
  },
}));

// ── Pending invite resolver ───────────────────────────────────────────────
//
// After sign-in (or magic-link acceptance), call the
// app.accept_pending_invites() RPC to attach this user to any
// organizations that had pending invitations matching their email.
// Multi-org friendly: one auth user can be a member of many orgs.
//
// We refresh the orgStore afterward so the active-org menu shows new
// memberships immediately.

async function resolvePendingInvites(): Promise<void> {
  if (!supabase) return;
  try {
    const { data, error } = await supabase.rpc('accept_pending_invites');
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[auth] accept_pending_invites failed:', error.message);
      return;
    }
    if (Array.isArray(data) && data.length > 0) {
      // eslint-disable-next-line no-console
      console.info('[auth] accepted invites:', data);
      // Dynamic import to avoid an authStore ⇄ orgStore cycle.
      const { useOrgStore } = await import('./orgStore');
      void useOrgStore.getState().fetchOrgs();
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[auth] accept_pending_invites threw:', e);
  }
}
